import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { EventCard } from "@/components/events/EventCard";
import RecentWinnersStrip from "@/components/events/RecentWinnersStrip";
import { Loader2, Plus } from "lucide-react";

function useEvents() {
  return useQuery({
    queryKey: ["events-all"],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/events", { params: { country: "NZ", upcoming: true, limit: 60 } });
      // Iter-291 §3 — preserve the whole envelope so we can render the
      // `sell_tickets_banner` above the grid.
      return {
        events: data?.events || (Array.isArray(data) ? data : []),
        sellBanner: data?.sell_tickets_banner || null,
      };
    },
    staleTime: 2 * 60_000,
  });
}

export default function EventsPage() {
  const q = useEvents();
  const { user } = useAuth();
  const events = q.data?.events || [];
  const sellBanner = q.data?.sellBanner || null;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title="Events" description="Upcoming events across New Zealand — concerts, markets, food festivals and more." />
      <Breadcrumbs items={[{ label: "Events" }]} />

      <div className="mt-6 flex items-start md:items-center justify-between gap-4 flex-col md:flex-row">
        <div>
          <h1 className="font-display text-4xl font-black text-slate-900">Events across Aotearoa</h1>
          <p className="mt-2 text-slate-500">Concerts, markets, food festivals and community gatherings.</p>
        </div>
        <div className="flex items-center gap-2">
          {user && (
            <Link
              to="/my/events"
              data-testid="events-page-my-events-link"
              className="rounded-full border border-soft text-slate-700 hover:border-[color:var(--brand-blue)] font-semibold px-4 py-2.5 text-sm"
            >
              My events
            </Link>
          )}
          <Link
            to="/events/post"
            data-testid="events-page-post-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-5 py-2.5 shadow-card"
          >
            <Plus className="w-4 h-4" /> Post your event
          </Link>
        </div>
      </div>

      {q.isLoading && <div className="mt-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}

      {/* Iter 659a — public Winner Wall, ABOVE the sell-tickets banner. */}
      <RecentWinnersStrip />

      {/* Iter-291 §3 — Sell tickets banner. Renders above the grid when
          the backend returns sell_tickets_banner in the events response. */}
      {sellBanner && (
        <div
          data-testid="events-sell-tickets-banner"
          className="mt-8 rounded-2xl bg-gradient-to-r from-[color:var(--brand-orange)] to-[#F26522] text-white p-5 md:p-6 flex flex-wrap items-start gap-4 shadow-card"
        >
          <div className="w-11 h-11 rounded-xl bg-white/15 grid place-items-center shrink-0 text-xl" aria-hidden>
            🎟
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="font-display text-lg md:text-xl font-black leading-tight">
              {sellBanner.title || "Organising an event?"}
            </div>
            {sellBanner.text && (
              <p className="mt-1 text-sm text-white/90 leading-relaxed">{sellBanner.text}</p>
            )}
          </div>
          {sellBanner.url && (
            <a
              href={sellBanner.url}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="events-sell-tickets-cta"
              className="inline-flex items-center gap-2 rounded-full bg-white text-[color:var(--brand-orange)] hover:bg-slate-100 font-bold text-sm px-4 py-2 whitespace-nowrap transition-colors"
            >
              {sellBanner.cta_label || "Sell tickets on allsale.events"}
            </a>
          )}
        </div>
      )}

      {!q.isLoading && events.length === 0 && (
        <div className="mt-12 rounded-2xl border border-dashed border-soft bg-white p-14 text-center text-slate-500">
          No upcoming events yet.
        </div>
      )}

      {/* Legacy-parity cards — same EventCard as the homepage row */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
        {events.map((e) => (
          <EventCard key={e.id} event={e} testId={`events-list-${e.id}`} />
        ))}
      </div>
    </div>
  );
}
